import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';

// Helper to get API base URL
const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';

function ContractUpload({ onUploadComplete, planType = 'free' }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [result, setResult] = useState(null);
  const [previewText, setPreviewText] = useState('');

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Validate file type
    if (!file.type.includes('pdf')) {
      setError('Please upload a PDF file');
      return;
    }

    // Validate file size (50MB limit)
    if (file.size > 50 * 1024 * 1024) {
      setError('File size must be less than 50MB');
      return;
    }

    setError(null);
    setAiError(null);
    setResult(null);
    setPreviewText('');
    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Use FormData for real file upload
      const formData = new FormData();
      formData.append('contract', file);
      formData.append('planType', planType);

      const response = await fetch(`${API_BASE}/upload`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const result = await response.json();

      if (!response.ok) {
        setError(result.error || 'Upload failed. Please try again.');
        setUploading(false);
        setUploadProgress(0);
        return;
      }

      // Check for AI summary fallback or error
      if (
        !result.summary ||
        result.summary === 'AI summary unavailable.' ||
        !result.redactedSummary ||
        result.redactedSummary === 'Preview unavailable.'
      ) {
        setAiError('AI analysis failed. You can try again later or contact support.');
      }

      setResult(result);
      setUploading(false);
      setUploadProgress(100);

      // Fetch preview summary if available
      if (result.jobId) {
        try {
          const previewResp = await fetch(`${API_BASE}/download?jobId=${encodeURIComponent(result.jobId)}&type=preview`);
          if (previewResp.ok) {
            const text = await previewResp.text();
            setPreviewText(text);
          } else {
            setPreviewText('Preview unavailable.');
          }
        } catch {
          setPreviewText('Preview unavailable.');
        }
      }

      // Wait a moment to show completion (optional: keep for jobId flow)
      if (onUploadComplete) onUploadComplete(result);

    } catch (err) {
      setError('Upload failed. Please try again.');
      setUploading(false);
      setUploadProgress(0);
    }
  }, [onUploadComplete, planType]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false,
    disabled: uploading
  });

  return (
    <div className="max-w-2xl mx-auto">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-blue-400'
        } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        
        {uploading ? (
          <div>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-lg font-medium text-gray-900 mb-2">
              Uploading your document...
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600">{uploadProgress}% complete</p>
          </div>
        ) : (
          <div>
            <div className="mx-auto h-16 w-16 text-gray-400 mb-4">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <p className="text-lg font-medium text-gray-900 mb-2">
              {isDragActive ? 'Drop your document here' : 'Upload your document'}
            </p>
            <p className="text-gray-600 mb-4">
              Drag and drop your PDF document, or click to browse
            </p>
            <p className="text-sm text-gray-500">
              Supports PDF files up to 50MB
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {aiError && (
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800 font-medium">{aiError}</p>
        </div>
      )}

      {/* Show preview summary if available */}
      {previewText && (
        <div className="mt-8 p-6 bg-gray-50 border border-gray-200 rounded-lg text-center">
          <div className="text-lg font-semibold text-gray-900 mb-2">Preview Summary</div>
          <div className="text-gray-700 whitespace-pre-line">{previewText}</div>
        </div>
      )}

      {/* Show risk score and download link if available */}
      {result && result.riskScore !== null && (
        <div className="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-800 mb-2">
            Risk Score: <span className="text-3xl">{result.riskScore}</span> / 10
          </div>
          {result.jobId && (
            <a
              href={`${API_BASE}/download?jobId=${encodeURIComponent(result.jobId)}&type=summary`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Download Full Summary
            </a>
          )}
        </div>
      )}

      <div className="mt-8 text-center">
        <p className="text-sm text-gray-600 font-semibold">
          Get Started for Free: Upload your document and receive a full summary and risk score for up to 3 pages—no payment required.
        </p>
      </div>
    </div>
  );
}

export default ContractUpload; 